import { useEffect } from 'react';
import type { Map as MapboxMap } from 'mapbox-gl';
import { useMapStore } from '../../../stores/mapStore';
import { getTileRoot } from '../../../lib/env';
import {
  FLOOD_FILL_PAINT,
  FLOOD_LAYER_MAX_ZOOM,
  FLOOD_TILE_MAX_ZOOM,
  FLOOD_TILE_MIN_ZOOM,
} from '../../../lib/floodExposure';

export const FLOOD_EXPOSURE_SOURCE_ID = 'ghana-flood-exposure';
export const FLOOD_EXPOSURE_FILL_LAYER = 'ghana-flood-exposure-fill';

const CLIMATOLOGY_PERIOD = 'climatology';

function tilesBase(): string {
  return getTileRoot();
}

function sourceMinZoom(map: MapboxMap, sourceId: string): number | undefined {
  const spec = map.getStyle()?.sources?.[sourceId];
  if (!spec || spec.type !== 'vector') return undefined;
  return typeof spec.minzoom === 'number' ? spec.minzoom : undefined;
}

function ensureFloodLayers(map: MapboxMap, visible: boolean) {
  const tileRoot = tilesBase();
  if (!tileRoot) return;

  const existingMinZoom = sourceMinZoom(map, FLOOD_EXPOSURE_SOURCE_ID);
  if (existingMinZoom !== undefined && existingMinZoom !== FLOOD_TILE_MIN_ZOOM) {
    if (map.getLayer(FLOOD_EXPOSURE_FILL_LAYER)) map.removeLayer(FLOOD_EXPOSURE_FILL_LAYER);
    if (map.getSource(FLOOD_EXPOSURE_SOURCE_ID)) map.removeSource(FLOOD_EXPOSURE_SOURCE_ID);
  }

  if (!map.getSource(FLOOD_EXPOSURE_SOURCE_ID)) {
    map.addSource(FLOOD_EXPOSURE_SOURCE_ID, {
      type: 'vector',
      tiles: [`${tileRoot}/tiles/flood_exposure/periods/${CLIMATOLOGY_PERIOD}/{z}/{x}/{y}.pbf`],
      minzoom: FLOOD_TILE_MIN_ZOOM,
      maxzoom: FLOOD_TILE_MAX_ZOOM,
    });
  }

  if (!map.getLayer(FLOOD_EXPOSURE_FILL_LAYER)) {
    const beforeId = map.getLayer('ghana-water') ? 'ghana-water' : undefined;
    map.addLayer(
      {
        id: FLOOD_EXPOSURE_FILL_LAYER,
        type: 'fill',
        source: FLOOD_EXPOSURE_SOURCE_ID,
        'source-layer': 'flood_exposure',
        minzoom: FLOOD_TILE_MIN_ZOOM,
        maxzoom: FLOOD_LAYER_MAX_ZOOM,
        layout: {
          visibility: visible ? 'visible' : 'none',
        },
        paint: FLOOD_FILL_PAINT,
      },
      beforeId,
    );
    return;
  }

  map.setLayerZoomRange(FLOOD_EXPOSURE_FILL_LAYER, FLOOD_TILE_MIN_ZOOM, FLOOD_LAYER_MAX_ZOOM);
  map.setLayoutProperty(FLOOD_EXPOSURE_FILL_LAYER, 'visibility', visible ? 'visible' : 'none');
}

/** Seasonal flood exposure climatology grid (CHIRPS multi-year index). */
export function useFloodExposureLayer(
  mapRef: React.MutableRefObject<MapboxMap | null>,
  floodExposureEnabled: boolean,
) {
  const { isMapLoaded } = useMapStore();

  useEffect(() => {
    const map = mapRef.current;
    if (!map || !isMapLoaded) return;

    const apply = () => {
      const activeMap = mapRef.current;
      if (!activeMap?.isStyleLoaded()) return;
      try {
        ensureFloodLayers(activeMap, floodExposureEnabled);
      } catch {
        // Style may still be loading layers.
      }
    };

    apply();
    map.on('style.load', apply);
    return () => {
      map.off('style.load', apply);
    };
  }, [mapRef, floodExposureEnabled, isMapLoaded]);
}
